import ConsumerHeader from '@/components/consumerHeader'
import GoToWaterTreeButton from '@/components/Analytics/GoToWaterTreeButton'
import { treeStateOfParent, type TreeButtonState } from '@/utils/premiseTree'
import {
  fetchSingleConsumer,
  type ConsumerFetchResult,
} from '@/services/consumerService'
import { Box, Tab } from '@mui/material'
import { Tabs } from '@mui/material'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom'

import { useAppTheme } from '@/theme/ThemeContext'
import { consumersReturnTarget } from '@/utils/consumersReturn'

const DETAILS_ROUTE = 'details'

function ConsumerDetailsLayout() {
  const { pathname, state } = useLocation()
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { consumerId, siteId } = useParams()
  const [value, setValue] = useState(0)
  const navigate = useNavigate()
  const handleChange = (_: unknown, newValue: number) => {
    setValue(newValue)
    navigate(`/${siteId}/consumers/${consumerId}/${options[newValue].route}`, {
      state,
    })
  }
  const [address, setAddress] = useState('')
  const [tree, setTree] = useState<TreeButtonState>({ status: 'loading' })
  const [excelExportHandler, setExcelExportHandler] = useState<
    (() => void) | null
  >(null)

  const [copyHandler, setCopyHandler] = useState<
    (() => void) | null
  >(null)

  const [pdfExportHandler, setPdfExportHandler] = useState<
    (() => void) | null
  >(null)

  // The tab effect below refetches on every tab change; only a new consumer clears the tree.
  useEffect(() => setTree({ status: 'loading' }), [consumerId])

  // The header shows what the same consumer read gives. The details tab makes that
  // read itself and hands it up, so the layout reads only on the other tabs.
  const showConsumer = useCallback((result: ConsumerFetchResult) => {
    if (!result.ok) {
      setTree({ status: 'failed' })
      return
    }
    const data = result.data
    let fetchedAddress = data.address || ''
    if (!fetchedAddress && data.resources) {
      const addressResource = data.resources.find(
        (r: any) => r.name === 'address' || r.name === 'כתובת'
      )
      if (addressResource) {
        fetchedAddress = addressResource.value
      }
    }

    setAddress(fetchedAddress || '')
    setTree(treeStateOfParent(data.parentId))
  }, [])

  const path = pathname.split('/').pop()
  useEffect(() => {
    const index = options.findIndex(option => option.route === path)
    if (index !== -1) {
      setValue(index)
    }

    if (path === DETAILS_ROUTE) return
    const accountId = localStorage.getItem('accountId')
    if (!accountId || !consumerId) return
    let current = true
    fetchSingleConsumer(accountId, consumerId).then(result => {
      if (current) showConsumer(result)
    })
    return () => {
      current = false
    }
  }, [consumerId, path, showConsumer])
  const options = useMemo(
    () => [
      { label: 'פרטי מד וצרכן', value: 0, route: 'details' },
      { label: 'קריאות', value: 1, route: `readings` },
      { label: 'התראות', value: 2, route: `notifications` },
      { label: 'היסטוריית החלפות', value: 3, route: `history` },
      { label: 'CRM', value: 4, route: `crm` },
      { label: 'מבט כללי', value: 5, route: `general` },
    ],
    []
  )
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        paddingTop: '16px',
        paddingBottom: '8px',
        paddingRight: '16px',
        paddingLeft: '30px',
      }}
    >
      <ConsumerHeader
        title={consumerId || ''}
        subTitle={address}
        variant="meterPage"
        onExcelExport={excelExportHandler ?? undefined}
        onCopy={copyHandler ?? undefined}
        onPdfExport={pdfExportHandler ?? undefined}
        backTo={consumersReturnTarget(state, siteId)}
        center={<GoToWaterTreeButton siteId={siteId} state={tree} />}
      />
      <Box
        sx={{
          borderBottom: 1,
          borderColor: isDarkMode ? '#374151' : 'divider',
          marginTop: '12px',
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          variant="scrollable"
          scrollButtons={false}
          sx={{
            direction: 'rtl',
            '& .MuiTabs-indicator': {
              backgroundColor: '#72A4EB',
              height: '3px',
            },
          }}
        >
          {options.map(option => (
            <Tab
              key={option.value}
              sx={{
                outline: 'none !important',
                color: isDarkMode ? '#FFFFFF' : '#003F61',
                textAlign: 'right',
                fontFamily: 'Rubik',
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '24px',
                minWidth: 0,
                px: 3,
                '&.Mui-selected': {
                  color: isDarkMode ? '#72A4EB' : '#003F61',
                },
              }}
              label={option.label}
              value={option.value}
            />
          ))}
        </Tabs>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Outlet context={{
            setExcelExportHandler,
            setCopyHandler,
            setPdfExportHandler,
            showConsumer,
          }} />
      </Box>
    </Box>
  )
}

export default ConsumerDetailsLayout
